// src/components/Footer.jsx
import React, { useEffect, useMemo, useState } from "react";
import { ref, onValue, get } from "firebase/database";
import { db } from "../lib/firebase";
import "./Footer.css";

import { CONFIG } from "../config";
import DynamicForm from "./DynamicForm";

/** ---- Config ---- */
const IMAGES_BASE = CONFIG.IMAGES_BASE;

const asUrl = (v) =>
  !v ? "" : /^https?:/i.test(v) ? v : `${IMAGES_BASE}/${encodeURIComponent(v)}`;
const toArray = (v) =>
  Array.isArray(v) ? v : v && typeof v === "object" ? Object.values(v) : [];
const tel = (t) => (t ? t.replace(/[^+\d]/g, "") : "");

export default function Footer() {
  const [cfg, setCfg] = useState(null);
  const [nav, setNav] = useState(null);
  const [contact, setContact] = useState(null);
  const [about, setAbout] = useState(null);

  const [footerForm, setFooterForm] = useState(null);        // selected form config
  const [footerFormLoading, setFooterFormLoading] = useState(false);

  /* Load footerconfig first */
  useEffect(() => {
    const off = onValue(ref(db, "footerconfig"), (s) =>
      setCfg(s.val() || null)
    );
    return () => off();
  }, []);

  /* Load related configs depending on toggles */
  useEffect(() => {
    if (!cfg) return;
    const off = [];

    if (cfg.displayLogo) {
      off.push(
        onValue(ref(db, "navconfig"), (s) => setNav(s.val() || null))
      );
    }

    // contactconfig is needed for contact, socials, locateus and for display data
    if (cfg.contactus || cfg.socialmedia || cfg.locateus || cfg.formConfigId) {
      off.push(
        onValue(ref(db, "contactconfig"), (s) =>
          setContact(s.val() || null)
        )
      );
    }

    if (cfg.aboutus) {
      off.push(
        onValue(ref(db, "aboutusconfig"), (s) => setAbout(s.val() || null))
      );
    }

    return () =>
      off.forEach((fn) => typeof fn === "function" && fn());
  }, [cfg]);

  /* Load selected footer form (from formConfigs) – only if a form is selected */
  useEffect(() => {
    const formId = cfg?.formConfigId;
    if (!formId) {
      setFooterForm(null);
      return;
    }

    let cancelled = false;
    setFooterFormLoading(true);

    (async () => {
      try {
        const snap = await get(ref(db, `formConfigs/${formId}`));
        if (!cancelled) {
          if (snap.exists()) {
            setFooterForm({ id: formId, ...snap.val() });
          } else {
            setFooterForm(null);
          }
        }
      } catch (e) {
        console.error("footer formConfig load error:", e);
        if (!cancelled) setFooterForm(null);
      } finally {
        if (!cancelled) setFooterFormLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [cfg?.formConfigId]);

  /* derived data */
  const brand = nav?.brand || "";

  const phones = useMemo(() => {
    if (!contact) return [];
    const arr = [];
    if (contact?.phone?.showOnFooter && contact.phone.number)
      arr.push(contact.phone);
    arr.push(
      ...toArray(contact?.phones).filter(
        (p) => p?.showOnFooter && p?.number
      )
    );
    return arr;
  }, [contact]);

  const emails = useMemo(() => {
    if (!contact) return [];
    const arr = [];
    if (contact?.email?.showOnFooter && contact.email.value)
      arr.push(contact.email);
    arr.push(
      ...toArray(contact?.emails).filter(
        (e) => e?.showOnFooter && e?.value
      )
    );
    return arr;
  }, [contact]);

  const socials = useMemo(
    () =>
      cfg?.socialmedia
        ? toArray(contact?.socialmedia).filter((s) => s?.showOnFooter)
        : [],
    [cfg?.socialmedia, contact]
  );

  const address =
    contact?.address || contact?.locationText || contact?.bannerText || "";
  const aboutText = about?.description || "";
  
  // Get CSS custom properties for blurred background
  const cssVariables = useMemo(() => {
    const vars = {};
    
    if (cfg?.useBackgroundImage && cfg?.backgroundImage && cfg?.imageBlur > 0) {
      const imageUrl = `${IMAGES_BASE}/${encodeURIComponent(cfg.backgroundImage)}`;
      vars['--footer-bg-image'] = `url(${imageUrl})`;
      vars['--footer-bg-blur'] = `${cfg.imageBlur}px`;
      vars['--footer-bg-opacity'] = cfg.imageOpacity || 1;
      vars['--footer-overlay-color'] = cfg.imageOverlayColor || '#000000';
      vars['--footer-overlay-opacity'] = cfg.imageOverlayOpacity || 0.5;
    }
    
    return vars;
  }, [cfg]);

  // Determine if we need blurred background class
  const needsBlurClass = cfg?.useBackgroundImage && cfg?.backgroundImage && cfg?.imageBlur > 0;

  const hasDynamicFooterForm = !!cfg?.formConfigId && !!footerForm;

  if (!cfg) return null;

  return (
    <>
      {/* Add CSS for blurred background if needed */}
      {needsBlurClass && (
        <style>
          {`
            .brand-footer.blurred-bg::before {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background-image: var(--footer-bg-image);
              background-size: cover;
              background-position: center;
              background-repeat: no-repeat;
              filter: blur(var(--footer-bg-blur));
              opacity: var(--footer-bg-opacity);
              z-index: -1;
            }
            
            .brand-footer.blurred-bg::after {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background-color: var(--footer-overlay-color);
              opacity: var(--footer-overlay-opacity);
              z-index: -1;
            }
            
            .brand-footer.blurred-bg {
              position: relative;
              background-color: transparent !important;
              z-index: 0;
            }
            
            .brand-footer.blurred-bg .brand-footer__grid,
            .brand-footer.blurred-bg .brand-footer__bottom {
              position: relative;
              z-index: 1;
            }
          `}
        </style>
      )}
      
      <footer 
        className={`brand-footer ${needsBlurClass ? 'blurred-bg' : ''}`}
        style={{
          // Set CSS variables
          ...cssVariables,
          // Set regular styles
          backgroundColor: (!cfg?.useBackgroundImage || !cfg?.backgroundImage) 
            ? cfg?.backgroundcolor || "#0d1238" 
            : 'transparent',
          color: '#fff',
          padding: '40px 20px 20px',
          position: 'relative',
          overflow: 'hidden',
          ...(cfg?.useBackgroundImage && cfg?.backgroundImage && (!cfg?.imageBlur || cfg.imageBlur === 0) && {
            backgroundImage: `linear-gradient(
              to bottom,
              ${cfg.imageOverlayColor}${Math.round((cfg.imageOverlayOpacity || 0.5) * 255).toString(16).padStart(2, '0')},
              ${cfg.imageOverlayColor}${Math.round((cfg.imageOverlayOpacity || 0.5) * 255).toString(16).padStart(2, '0')}
            ), url(${IMAGES_BASE}/${encodeURIComponent(cfg.backgroundImage)})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            opacity: cfg.imageOpacity || 1,
          })
        }}
      >
        <div className="brand-footer__grid">
          {/* ABOUT US */}
          {cfg.aboutus && (
            <div className="bf-col">
              <h4 className="bf-title">ABOUT US</h4>
              {aboutText && <p className="bf-text">{aboutText}</p>}
              <a href="/about" className="bf-btn">
                Read More
              </a>
            </div>
          )}

          {/* LOCATE US */}
          {cfg.locateus && address && (
            <div className="bf-col">
              <h4 className="bf-title">LOCATE US</h4>
              <p className="bf-text">{address}</p>
            </div>
          )}

          {/* LET'S TALK */}
          {(cfg.contactus || cfg.socialmedia) && (
            <div className="bf-col">
              <h4 className="bf-title">LET'S TALK</h4>
              {cfg.contactus && emails[0] && (
                <p className="bf-line">
                  <strong>Email:</strong>
                  <br />
                  <a
                    className="bf-link"
                    href={`mailto:${emails[0].value}`}
                  >
                    {emails[0].value}
                  </a>
                </p>
              )}
              {cfg.contactus && phones[0] && (
                <p className="bf-line">
                  <strong>Phone:</strong>
                  <br />
                  <a
                    className="bf-link"
                    href={`tel:${tel(phones[0].number)}`}
                  >
                    {phones[0].number}
                  </a>
                </p>
              )}
              {cfg.socialmedia && socials.length > 0 && (
                <>
                  <p className="bf-line">
                    <strong>Follow Us:</strong>
                  </p>
                  <div className="bf-socials">
                    {socials.map((s, i) => (
                      <a
                        key={i}
                        href={s.link}
                        target="_blank"
                        rel="noreferrer"
                        className="bf-social"
                      >
                        {s.icon ? (
                          <img src={asUrl(s.icon)} alt={s.platform || "Social icon"} />
                        ) : (
                          s.platform
                        )}
                      </a>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ENQUIRE – ONLY if admin selected a form in footerconfig */}
          {cfg.formConfigId && (
            <div className="bf-col">
              {/* If no form loaded (invalid id / still loading), we hide content */}
              {footerFormLoading ? (
                <div className="bf-form-loading">Loading form…</div>
              ) : hasDynamicFooterForm ? (
                <>
                  <h4 className="bf-title">
                    {footerForm.formTitle || "ENQUIRE"}
                  </h4>
                  <DynamicForm form={footerForm} />
                </>
              ) : null}
            </div>
          )}
        </div>

        <div className="brand-footer__bottom">
          <div className="bf-copy">
            © {new Date().getFullYear()} {brand ? `${brand}. ` : ""}All rights reserved.
          </div>
          <div className="bf-creator">
            Creator:&nbsp;
            <a
              href="https://www.adityasureshpatil.com/home"
              target="_blank"
              rel="noreferrer"
            >
              Aditya S Patil
            </a>
          </div>
        </div>
      </footer>
    </>
  );
}
